import { useState, FormEvent, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../hooks/useAuth';
// Login is an intentionally always-dark branded hero (in both color modes).
// Pull the values from the shared tokens so there's a single source of truth.
import { BUTTON_GRADIENT as SSO_GRADIENT, PAGE_GRADIENT_DARK as PAGE_GRADIENT, LINK_YELLOW } from '../theme/tokens';
import heroBg from '../images/hero-bg.jpg';

const ssoButtonSx = {
    width: '100%',
    height: 50,
    fontSize: 18,
    fontWeight: 500,
    color: '#fff',
    background: SSO_GRADIENT,
    border: '2px solid #f3bc16',
    borderRadius: '10px',
    textAlign: 'left',
    justifyContent: 'flex-start',
    pl: 2,
    pr: 2,
    textTransform: 'none',
    transition: 'transform 0.2s ease',
    '& .bi': { fontSize: 22, mr: 1.5 },
    '&:hover': {
        background: SSO_GRADIENT,
        transform: 'scale(1.02)',
    },
} as const;

const inputSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '6px',
    },
};

const providers = [
    { id: 'google', label: 'Sign in with Google', icon: 'bi-google' },
    { id: 'microsoft', label: 'Sign in with Microsoft', icon: 'bi-microsoft' },
    { id: 'linkedin', label: 'Sign in with LinkedIn', icon: 'bi-linkedin' },
    { id: 'facebook', label: 'Sign in with Facebook', icon: 'bi-facebook' },
];

// Accept only same-origin, relative paths to prevent open-redirect via ?next=.
function sanitizeNext(raw: string | null | undefined): string | null {
    if (!raw) return null;
    if (!raw.startsWith('/') || raw.startsWith('//')) return null;
    return raw;
}

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const loginBoxRef = useRef<HTMLDivElement>(null);

    const next = useMemo(() => {
        const fromQuery = sanitizeNext(new URLSearchParams(location.search).get('next'));
        if (fromQuery) return fromQuery;
        const fromState = (location.state as { from?: string } | null)?.from;
        return sanitizeNext(fromState);
    }, [location.search, location.state]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        const result = await login(email, password);
        setLoading(false);
        if (result.success) {
            navigate(next ?? '/');
        } else {
            setError(result.message ?? 'Login failed');
        }
    };

    const scrollToLogin = () => loginBoxRef.current?.scrollIntoView({ behavior: 'smooth' });

    return (
        <Box>
            {/* Hero — banner image behind a purple (bg-primary) panel, white text */}
            <Box
                component="section"
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: { xs: 380, md: 520 },
                    bgcolor: 'primary.main',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {/* section_bg: banner image + dark top-down overlay (matches legacy) */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        backgroundImage: `linear-gradient(180deg, #0d0e20 0%, rgba(13, 14, 32, 0.1) 100%), url(${heroBg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'bottom center',
                        backgroundRepeat: 'no-repeat',
                    }}
                />
                <Container sx={{ position: 'relative', zIndex: 20, py: { xs: 6, md: 8 } }}>
                    <Box maxWidth={{ xs: '100%', md: '83.333%' }}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ fontWeight: 800, mb: 3, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.15 }}
                        >
                            Issue and Earn Open Badges for Free with the platform that lets you acknowledge
                            Learning where it lives.... Everywhere
                        </Typography>
                        <Typography sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6, maxWidth: 760, color: 'rgba(255,255,255,0.92)' }}>
                            Introducing Thinkingcap Everywhere, a revolutionary and patent pending browser
                            extension and mobile app that transforms online learning experiences without
                            requiring any website modifications or development. With Thinkingcap Everywhere,
                            users seamlessly discover and engage with curated learning activities integrated
                            directly into existing web pages.
                        </Typography>
                        <Button
                            onClick={scrollToLogin}
                            variant="contained"
                            sx={{ height: 48, px: 3, fontSize: 16, borderRadius: '10px' }}
                            endIcon={<Box component="i" className="bi bi-arrow-down" />}
                        >
                            Register / Log In
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Login box */}
            <Box
                component="section"
                id="logInBox"
                ref={loginBoxRef}
                sx={{ background: PAGE_GRADIENT, color: '#fff', px: 2, py: { xs: 5, md: 7 } }}
            >
                <Container maxWidth="sm">
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography variant="h4" component="h2" fontWeight={700} mb={4} color="#fff">
                            Register / Log In
                        </Typography>

                        <Box width="100%" maxWidth={420}>
                            <Box display="flex" flexDirection="column" gap={1.25} mb={2}>
                                {providers.map((p) => (
                                    <Button
                                        key={p.id}
                                        component="a"
                                        href={next ? `/auth/${p.id}?returnTo=${encodeURIComponent(next)}` : `/auth/${p.id}`}
                                        sx={ssoButtonSx}
                                        startIcon={<Box component="i" className={`bi ${p.icon}`} />}
                                    >
                                        {p.label}
                                    </Button>
                                ))}
                            </Box>

                            <Box display="flex" alignItems="center" gap={2} my={3}>
                                <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.3)" />
                                <Typography variant="body2" sx={{ color: '#fff' }}>OR</Typography>
                                <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.3)" />
                            </Box>

                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                            <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                                <Box>
                                    <Typography component="label" htmlFor="email" variant="body1" sx={{ color: '#fff', display: 'block', mb: 0.5 }}>
                                        Email:
                                    </Typography>
                                    <TextField
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoComplete="email"
                                        size="small"
                                        fullWidth
                                        sx={inputSx}
                                    />
                                </Box>
                                <Box>
                                    <Typography component="label" htmlFor="password" variant="body1" sx={{ color: '#fff', display: 'block', mb: 0.5 }}>
                                        Password:
                                    </Typography>
                                    <TextField
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete="current-password"
                                        size="small"
                                        fullWidth
                                        sx={inputSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        onClick={() => setShowPassword((v) => !v)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        <Box component="i" className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} sx={{ color: '#555' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Button
                                    type="submit"
                                    disabled={loading}
                                    sx={{
                                        ...ssoButtonSx,
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        pl: 2,
                                        mt: 1,
                                    }}
                                >
                                    {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                                    Log In
                                </Button>
                            </Box>

                            <Box display="flex" justifyContent="space-between" mt={2}>
                                <Box component={Link} to="/register" sx={{ color: LINK_YELLOW, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    Create Account
                                </Box>
                                <Box component={Link} to="/forgot-password" sx={{ color: LINK_YELLOW, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    Forgot Password
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
